import { NextApiRequestQuery } from 'next/dist/server/api-utils';
import { TFunction } from 'i18next';
import {
    CompetitionInterface,
    CompetitionYearInterface,
    FiltersInterface,
    FilterSize,
    LabelSize,
    Option,
} from 'components/interfaces/GeneralInterface';
import { getMainFilter } from 'components/tables/filters/Filters';
import { MATCH_FILTER_KEYS } from 'constants/filterKeysConstants';
import { ClubListItemInterface } from 'components/interfaces/ClubInterface';
import { getMatchesState } from 'utils/helpers/match';
import { FieldTypeApiEnum } from 'submodules/api_middleware';
import {
    findCompetitions,
    findClubsByInternalId,
    transformClubsToOptions,
} from 'utils/helpers/browser/filter';
import { ROW_BREAK } from 'components/editForm/utils/items';

interface Props {
    t: TFunction;
    clubList: ClubListItemInterface[];
    competitionList: CompetitionInterface[];
    competitionYears: CompetitionYearInterface[];
    showSearchBtn?: boolean;
    filterInitQuery?: NextApiRequestQuery;
}

const getRoundOptions = (t: TFunction) => {
    const roundsOptions: Option[] = [];
    roundsOptions.push({ label: t('Match.actual_round'), value: 'current' });
    for (let i = 1; i <= 100; i++) {
        const val = i + '';
        roundsOptions.push({
            label: val,
            value: val,
        });
    }
    return roundsOptions;
};

export const getMatchFilters = ({
    t,
    clubList,
    competitionList,
    competitionYears,
    showSearchBtn,
    filterInitQuery,
}: Props): FiltersInterface => ({
    showSearchBtn,
    fixed: {
        mainField: getMainFilter('MatchesPage.searchPlaceholder', 'query'),
        showAdvancedBtn: true,
        initQuery: filterInitQuery,
    },
    advanced: [
        {
            name: MATCH_FILTER_KEYS.competition_year,
            label: 'filters.year',
            type: FieldTypeApiEnum.SELECT,
            size: FilterSize.S20,
            labelSize: LabelSize.ML,
            values: competitionYears.map((year) => ({
                label: year.season,
                value: String(year.year),
            })),
        },
        {
            name: MATCH_FILTER_KEYS.leagues,
            label: 'MemberProfile.league',
            type: FieldTypeApiEnum.AUTOCOMPLETE,
            size: FilterSize.S20,
            placeholder: 'filters.findByNameNumber',
            values: competitionList.map(
                (competition: CompetitionInterface) => ({
                    label: `${competition.name} (${competition.identifier})`,
                    value: competition.id,
                })
            ),
            onSearch: (query, state) =>
                findCompetitions({
                    ...query,
                    year: state[MATCH_FILTER_KEYS.competition_year],
                }),
            labelSize: LabelSize.ML,
            reInitOnFields: [MATCH_FILTER_KEYS.competition_year],
            initValuesCb: (_, state) =>
                findCompetitions({
                    year: state[MATCH_FILTER_KEYS.competition_year],
                }),
        },
        {
            name: MATCH_FILTER_KEYS.state,
            label: 'general.result',
            type: FieldTypeApiEnum.SELECT,
            size: FilterSize.S20,
            values: getMatchesState(t),
            labelSize: LabelSize.ML,
        },
        ROW_BREAK,
        {
            name: MATCH_FILTER_KEYS.home_club,
            label: 'pages.home',
            type: FieldTypeApiEnum.AUTOCOMPLETE,
            size: FilterSize.S20,
            placeholder: 'filters.findByNameId',
            values: transformClubsToOptions(clubList, 'internal_id'),
            onSearch: (value, state) =>
                findClubsByInternalId({
                    ...value,
                    competition_id: state[MATCH_FILTER_KEYS.leagues],
                }),
            onSearchProperty: 'internal_id',
            labelSize: LabelSize.ML,
            reInitOnFields: [MATCH_FILTER_KEYS.leagues],
            initValuesCb: (_, state) =>
                findClubsByInternalId({
                    competition_id: state[MATCH_FILTER_KEYS.leagues],
                }),
        },
        {
            name: MATCH_FILTER_KEYS.visitor_club,
            label: 'pages.visitor',
            type: FieldTypeApiEnum.AUTOCOMPLETE,
            size: FilterSize.S20,
            placeholder: 'filters.findByNameId',
            values: transformClubsToOptions(clubList, 'internal_id'),
            onSearch: (value, state) =>
                findClubsByInternalId({
                    ...value,
                    competition_id: state[MATCH_FILTER_KEYS.leagues],
                }),
            onSearchProperty: 'internal_id',
            labelSize: LabelSize.ML,
            reInitOnFields: [MATCH_FILTER_KEYS.leagues],
            initValuesCb: (_, state) =>
                findClubsByInternalId({
                    competition_id: state[MATCH_FILTER_KEYS.leagues],
                }),
        },
        {
            name: MATCH_FILTER_KEYS.round,
            label: 'MatchesPage.round',
            type: FieldTypeApiEnum.SELECT,
            size: FilterSize.S20,
            values: getRoundOptions(t),
            labelSize: LabelSize.ML,
        },
        ROW_BREAK,
        {
            name: MATCH_FILTER_KEYS.date_from,
            label: 'MatchesPage.dateFrom',
            type: FieldTypeApiEnum.DATE,
            size: FilterSize.S20,
            labelSize: LabelSize.ML,
        },
        {
            name: MATCH_FILTER_KEYS.date_to,
            label: 'MatchesPage.dateTo',
            type: FieldTypeApiEnum.DATE,
            size: FilterSize.S20,
            labelSize: LabelSize.ML,
        },
        {
            name: MATCH_FILTER_KEYS.has_protest_home,
            label: 'MatchesPage.has_protest_home',
            type: FieldTypeApiEnum.CHECKBOX,
            size: FilterSize.S10,
            labelSize: LabelSize.ML,
        },
        {
            name: MATCH_FILTER_KEYS.has_protest_guest,
            label: 'MatchesPage.has_protest_guest',
            type: FieldTypeApiEnum.CHECKBOX,
            size: FilterSize.S10,
            labelSize: LabelSize.ML,
        },
    ],
});
